import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';

const API_BASE = '/api';

function apiRequest<T>(path: string, options?: RequestInit): Promise<T> {
  const token = localStorage.getItem('auth_token');
  return fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(options?.headers || {}) }
  }).then(async (res) => {
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro desconhecido');
    return data as T;
  });
}

type StepType = 'text' | 'image' | 'audio' | 'video' | 'document';

interface Step {
  id?: string;
  order: number;
  type: StepType;
  content: string;
  caption: string;
  delayMs: number;
}

interface QuickReply {
  id: string;
  name: string;
  description: string;
  steps: Step[];
}

const STEP_TYPE_LABELS: Record<StepType, string> = {
  text: 'Texto',
  image: 'Imagem',
  audio: 'Áudio',
  video: 'Vídeo',
  document: 'Documento'
};

const STEP_ICONS: Record<StepType, string> = {
  text: '💬',
  image: '🖼️',
  audio: '🎵',
  video: '🎬',
  document: '📄'
};

const DELAY_OPTIONS = [
  { value: 0, label: 'Sem delay' },
  { value: 500, label: '0,5s' },
  { value: 1000, label: '1s' },
  { value: 2000, label: '2s' },
  { value: 3000, label: '3s' },
  { value: 5000, label: '5s' },
  { value: 8000, label: '8s' },
  { value: 10000, label: '10s' },
];

function emptyStep(order: number): Step {
  return { order, type: 'text', content: '', caption: '', delayMs: 0 };
}

function StepCard({ step, index, total, onChange, onRemove, onMove, onUpload }: {
  step: Step;
  index: number;
  total: number;
  onChange: (s: Step) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
  onUpload: (index: number, file: File) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const isMedia = step.type !== 'text';

  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
      {/* Step header */}
      <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
        <span className="text-base">{STEP_ICONS[step.type]}</span>
        <span className="text-xs font-semibold text-slate-600">Passo {index + 1}</span>
        <div className="ml-auto flex items-center gap-1">
          {/* Delay */}
          <select
            value={step.delayMs}
            onChange={(e) => onChange({ ...step, delayMs: Number(e.target.value) })}
            className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-xs text-slate-600"
            title="Delay antes deste passo"
          >
            {DELAY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.value === 0 ? 'Sem delay' : `⏱ ${o.label}`}</option>
            ))}
          </select>
          {/* Type selector */}
          <select
            value={step.type}
            onChange={(e) => onChange({ ...step, type: e.target.value as StepType, content: '', caption: '' })}
            className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-xs text-slate-600"
          >
            {(Object.keys(STEP_TYPE_LABELS) as StepType[]).map((t) => (
              <option key={t} value={t}>{STEP_ICONS[t]} {STEP_TYPE_LABELS[t]}</option>
            ))}
          </select>
          {/* Move up/down */}
          <button
            onClick={() => onMove(-1)}
            disabled={index === 0}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30"
            title="Mover para cima"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 15l-6-6-6 6"/></svg>
          </button>
          <button
            onClick={() => onMove(1)}
            disabled={index === total - 1}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30"
            title="Mover para baixo"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6"/></svg>
          </button>
          <button onClick={onRemove} className="rounded p-1 text-red-400 hover:bg-red-50" title="Remover passo">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>
      </div>

      {/* Step body */}
      <div className="p-3 space-y-2">
        {step.type === 'text' ? (
          <textarea
            value={step.content}
            onChange={(e) => onChange({ ...step, content: e.target.value })}
            placeholder="Digite o texto da mensagem..."
            rows={3}
            className="w-full resize-none rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-200"
          />
        ) : (
          <div className="space-y-2">
            {/* URL or upload */}
            <div className="flex gap-2">
              <input
                type="text"
                value={step.content}
                onChange={(e) => onChange({ ...step, content: e.target.value })}
                placeholder={`URL do arquivo de ${STEP_TYPE_LABELS[step.type].toLowerCase()}...`}
                className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-200"
              />
              <button
                onClick={() => fileRef.current?.click()}
                className="flex items-center gap-1.5 rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-600 hover:bg-slate-100"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                Upload
              </button>
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                accept={step.type === 'image' ? 'image/*' : step.type === 'audio' ? 'audio/*' : step.type === 'video' ? 'video/*' : '*'}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(index, f); e.target.value = ''; }}
              />
            </div>
            {/* Preview */}
            {step.content && step.type === 'image' && (
              <img src={step.content} alt="preview" className="h-20 rounded object-cover border border-slate-200" />
            )}
            {step.content && step.type === 'audio' && (
              <audio src={step.content} controls className="w-full h-8" />
            )}
            {step.content && step.type === 'video' && (
              <video src={step.content} controls className="h-24 rounded border border-slate-200" />
            )}
            {/* Caption */}
            {(step.type === 'image' || step.type === 'video' || step.type === 'document') && (
              <input
                type="text"
                value={step.caption}
                onChange={(e) => onChange({ ...step, caption: e.target.value })}
                placeholder="Legenda (opcional)..."
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-200"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function QuickReplyEditor({ item, onSave, onCancel }: {
  item: QuickReply | null;
  onSave: (data: { name: string; description: string; steps: Step[] }) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(item?.name || '');
  const [description, setDescription] = useState(item?.description || '');
  const [steps, setSteps] = useState<Step[]>(item?.steps?.length ? item.steps : [emptyStep(0)]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const addStep = () => setSteps((prev) => [...prev, emptyStep(prev.length)]);

  const updateStep = (i: number, s: Step) => setSteps((prev) => prev.map((x, idx) => idx === i ? s : x));
  const removeStep = (i: number) => setSteps((prev) => prev.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, order: idx })));
  const moveStep = (i: number, dir: -1 | 1) => {
    const next = [...steps];
    const swapIdx = i + dir;
    if (swapIdx < 0 || swapIdx >= next.length) return;
    [next[i], next[swapIdx]] = [next[swapIdx], next[i]];
    setSteps(next.map((s, idx) => ({ ...s, order: idx })));
  };

  const handleUpload = async (stepIndex: number, file: File) => {
    setUploading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/media/upload', { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro no upload');
      updateStep(stepIndex, { ...steps[stepIndex], content: data.fileUrl });
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    const slug = name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    if (!slug) { toast.error('Nome inválido. Use apenas letras, números e underscore.'); return; }
    if (!steps.length) { toast.error('Adicione pelo menos um passo.'); return; }
    const hasEmpty = steps.some((s) => !s.content.trim());
    if (hasEmpty) { toast.error('Preencha o conteúdo de todos os passos.'); return; }
    setSaving(true);
    try {
      await onSave({ name: slug, description, steps });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Editor header */}
      <div className="flex items-center gap-3 border-b border-slate-200 px-6 py-4">
        <button onClick={onCancel} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
        <h2 className="text-base font-semibold text-slate-900">{item ? 'Editar Resposta Rápida' : 'Nova Resposta Rápida'}</h2>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
        {/* Name & description */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">
              Nome do atalho <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center rounded-md border border-slate-300 bg-white focus-within:border-violet-400 focus-within:ring-1 focus-within:ring-violet-200">
              <span className="pl-3 text-slate-400 text-sm font-medium">/</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value.replace(/\s+/g, '_'))}
                placeholder="nome_do_atalho"
                className="flex-1 rounded-md bg-transparent px-2 py-2.5 text-sm text-slate-800 outline-none placeholder-slate-400"
              />
            </div>
            <p className="mt-1 text-[11px] text-slate-400">Será ativado digitando /{name || 'nome'} no chat</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Descrição</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex: Mensagem de boas-vindas..."
              className="w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-200"
            />
          </div>
        </div>

        {/* Steps */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700">Passos do fluxo</h3>
            <span className="text-xs text-slate-400">{steps.length} passo{steps.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="space-y-3">
            {steps.map((step, i) => (
              <StepCard
                key={i}
                step={step}
                index={i}
                total={steps.length}
                onChange={(s) => updateStep(i, s)}
                onRemove={() => removeStep(i)}
                onMove={(dir) => moveStep(i, dir)}
                onUpload={handleUpload}
              />
            ))}
          </div>
          <button
            onClick={addStep}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 py-3 text-sm text-slate-500 hover:border-violet-400 hover:text-violet-600 transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
            Adicionar passo
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
        <button onClick={onCancel} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={saving || uploading}
          className="flex items-center gap-2 rounded-lg bg-violet-600 px-5 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-60"
        >
          {saving ? (
            <><svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>Salvando...</>
          ) : uploading ? 'Enviando arquivo...' : '✓ Salvar'}
        </button>
      </div>
    </div>
  );
}

export function QuickRepliesPage() {
  const [items, setItems] = useState<QuickReply[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<QuickReply | null | 'new'>(null);
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiRequest<QuickReply[]>('/quick-replies');
      setItems(data);
    } catch {
      toast.error('Erro ao carregar respostas rápidas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (data: { name: string; description: string; steps: Step[] }) => {
    try {
      if (editing === 'new') {
        await apiRequest('/quick-replies', { method: 'POST', body: JSON.stringify(data) });
        toast.success('Resposta rápida criada!');
      } else if (editing) {
        await apiRequest(`/quick-replies/${editing.id}`, { method: 'PUT', body: JSON.stringify(data) });
        toast.success('Resposta rápida atualizada!');
      }
      setEditing(null);
      load();
    } catch (err: any) {
      toast.error(err.message);
      throw err;
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Apagar esta resposta rápida?')) return;
    setDeleting(id);
    try {
      await apiRequest(`/quick-replies/${id}`, { method: 'DELETE' });
      toast.success('Removida');
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setDeleting(null);
    }
  };

  const filtered = items.filter((x) =>
    x.name.includes(search.toLowerCase()) || (x.description || '').toLowerCase().includes(search.toLowerCase())
  );

  if (editing !== null) {
    return (
      <div className="flex h-[calc(100vh-44px)] flex-col bg-white">
        <QuickReplyEditor
          item={editing === 'new' ? null : editing}
          onSave={handleSave}
          onCancel={() => setEditing(null)}
        />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-44px)] bg-slate-50 p-6">
      <div className="mx-auto max-w-3xl">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Respostas Rápidas</h1>
            <p className="mt-0.5 text-sm text-slate-500">
              Cadastre fluxos de mensagens e acesse com <code className="rounded bg-slate-100 px-1 py-0.5 text-violet-700">/nome</code> no chat
            </p>
          </div>
          <button
            onClick={() => setEditing('new')}
            className="flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
            Nova resposta
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome ou descrição..."
            className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-4 text-sm text-slate-800 placeholder-slate-400 shadow-sm focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-200"
          />
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <svg className="animate-spin mr-2" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
            Carregando...
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 py-16 text-center">
            <span className="text-4xl mb-3">⚡</span>
            <p className="text-sm font-medium text-slate-600">{search ? 'Nenhuma resposta encontrada' : 'Nenhuma resposta rápida cadastrada'}</p>
            {!search && (
              <button onClick={() => setEditing('new')} className="mt-4 rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700">
                Criar primeira resposta
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((item) => (
              <div key={item.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:border-violet-300 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center rounded-md bg-violet-100 px-2 py-0.5 text-sm font-semibold text-violet-700">/{item.name}</span>
                      {item.description && <span className="text-sm text-slate-500 truncate">{item.description}</span>}
                    </div>
                    {/* Steps preview */}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {item.steps.map((s, i) => (
                        <span key={i} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-500">
                          {STEP_ICONS[s.type as StepType]}
                          {STEP_TYPE_LABELS[s.type as StepType]}
                          {s.delayMs > 0 && <span className="text-slate-400">+{s.delayMs / 1000}s</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => setEditing(item)}
                      className="rounded-md px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      disabled={deleting === item.id}
                      className="rounded-md px-3 py-1.5 text-xs font-medium text-red-500 hover:bg-red-50 disabled:opacity-40"
                    >
                      {deleting === item.id ? '...' : 'Apagar'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
